import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import debounce from "debounce-promise";
import { produce } from "immer";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import * as yup from "yup";

import Button from "@components/buttons/Button";
import BaseErrors from "@components/fields/BaseErrors";
import BooleanRadioButtonGroup from "@components/fields/BooleanRadioButtonGroup";
import CareProfileInput from "@components/fields/CareProfileInput";
import Select from "@components/fields/Select";
import styles from "@components/forms/careProfile/CareProfile.module.css";
import CareProfileFormCondition from "@components/forms/careProfile/CareProfileFormCondition";
import CareProfileRollupItem from "@components/forms/careProfile/CareProfileRollupItem";
import DiagnosisForm from "@components/forms/DiagnosisForm";
import Form from "@components/forms/Form";
import MedicationForm from "@components/forms/MedicationForm";
import ProviderForm from "@components/forms/ProviderForm";
import Modal, { ModalElement } from "@components/Modal";
import IconBase from "@components/text/IconBase";
import BodyCopy from "@components/typography/BodyCopy";
import useDestroyDiagnosis from "@hooks/mutations/useDestroyDiagnosis";
import useDestroyMedication from "@hooks/mutations/useDestroyMedication";
import useDestroyProvider from "@hooks/mutations/userDestroyProvider";
import useOpener from "@hooks/useOpener";
import {
  CareProfileDevelopmentalOrEducationalConcernEnum,
  CareProfileFieldNameEnum,
  CareProfileFieldScopeEnum,
  CareProfileMedicationManagementStatusEnum,
  CareProfileUpdateDocument,
  CareProfileUpdateMutation,
  CareProfileUpdateMutationVariables,
  GetCareProfileFieldRulesQuery,
  GetJourneyWithCareProfileQuery,
  MedicationFormatEnum,
  SeverityEnum,
  YesNoDontKnowEnum
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";
import {
  optionsForBoolean,
  optionsForDevelopmentalOrEducationalConcerns,
  optionsForMedicationManagementStatus,
  optionsForSeverity,
  optionsForYesNoDontKnow
} from "@utils/optionUtils";

type CareProfileFieldRules = GetCareProfileFieldRulesQuery["careProfileFieldRules"];

type Props = {
  careProfileFieldRules: CareProfileFieldRules;
  journey: GetJourneyWithCareProfileQuery["journey"];
};

type Diagnosis = GetJourneyWithCareProfileQuery["journey"]["diagnoses"][number];
type Medication = GetJourneyWithCareProfileQuery["journey"]["medications"][number];
type Provider = GetJourneyWithCareProfileQuery["journey"]["providers"][number];

type FormData = {
  developmentalOrEducationalConcerns?: CareProfileDevelopmentalOrEducationalConcernEnum[];
  developmentalOrEducationalConcernsOther?: string | null;
  highRiskPregnancy?: YesNoDontKnowEnum | null;
  lifeThreateningCondition?: YesNoDontKnowEnum | null;
  longTermCondition?: YesNoDontKnowEnum | null;
  medicationManagementStatus?: CareProfileMedicationManagementStatusEnum | null;
  medicationsAffordable?: YesNoDontKnowEnum | null;
  painSeverity?: SeverityEnum | null;
  pastYearHospitalizations?: number | string | null;
  recentHospitalization?: boolean | null;
  recentHospitalizationSynopsis?: string | null;
  recentIncident?: YesNoDontKnowEnum | null;
};

const CareProfileMedicalForm = ({ careProfileFieldRules, journey }: Props) => {
  const intl = useIntl();

  const medicationOpener = useOpener();
  const medicationModalRef = useRef<ModalElement>(null);
  const [editingMedication, setEditingMedication] = useState<Medication>();

  const providerOpener = useOpener();
  const providerModalRef = useRef<ModalElement>(null);
  const [editingProvider, setEditingProvider] = useState<Provider>();

  const diagnosisOpener = useOpener();
  const diagnosisModalRef = useRef<ModalElement>(null);
  const [editingDiagnosis, setEditingDiagnosis] = useState<Diagnosis>();

  const careProfile = journey.careProfile;

  const defaultValues: FormData = {
    developmentalOrEducationalConcerns: careProfile.developmentalOrEducationalConcerns ?? [],
    developmentalOrEducationalConcernsOther: careProfile.developmentalOrEducationalConcernsOther ?? null,
    highRiskPregnancy: careProfile.highRiskPregnancy ?? null,
    lifeThreateningCondition: careProfile.lifeThreateningCondition ?? null,
    longTermCondition: careProfile.longTermCondition ?? null,
    medicationManagementStatus: careProfile.medicationManagementStatus ?? null,
    medicationsAffordable: careProfile.medicationsAffordable ?? null,
    painSeverity: careProfile.painSeverity ?? null,
    pastYearHospitalizations: careProfile.pastYearHospitalizations ?? null,
    recentHospitalization: careProfile.recentHospitalization ?? null,
    recentHospitalizationSynopsis: careProfile.recentHospitalizationSynopsis ?? null,
    recentIncident: careProfile.recentIncident ?? null
  };

  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [updateCareProfile] = useMutation<CareProfileUpdateMutation, CareProfileUpdateMutationVariables>(
    CareProfileUpdateDocument
  );

  const destroyDiagnosis = useDestroyDiagnosis(journey.id);
  const destroyMedication = useDestroyMedication(journey.id);
  const destroyProvider = useDestroyProvider(journey.id);

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      developmentalOrEducationalConcerns: yup
        .array()
        .of(yup.string().oneOf(Object.values(CareProfileDevelopmentalOrEducationalConcernEnum)).defined()),
      developmentalOrEducationalConcernsOther: yup.string().nullable(),
      highRiskPregnancy: yup.string().oneOf(Object.values(YesNoDontKnowEnum)).nullable(),
      lifeThreateningCondition: yup.string().oneOf(Object.values(YesNoDontKnowEnum)).nullable(),
      longTermCondition: yup.string().oneOf(Object.values(YesNoDontKnowEnum)).nullable(),
      medicationManagementStatus: yup
        .string()
        .oneOf(Object.values(CareProfileMedicationManagementStatusEnum))
        .nullable(),
      medicationsAffordable: yup.string().oneOf(Object.values(YesNoDontKnowEnum)).nullable(),
      painSeverity: yup.string().oneOf(Object.values(SeverityEnum)).nullable(),
      pastYearHospitalizations: yup.lazy(value => {
        if (value === "") {
          return yup.string().nullable();
        }
        return yup
          .number()
          .nullable()
          .min(0, intl.formatMessage({ id: "errors.careProfile.pastYearHospitalizations.greaterThanOrEqualTo" }))
          .max(500, intl.formatMessage({ id: "errors.careProfile.pastYearHospitalizations.tooLarge" }))
          .integer(intl.formatMessage({ id: "errors.careProfile.pastYearHospitalizations.integer" }))
          .typeError(intl.formatMessage({ id: "errors.careProfile.pastYearHospitalizations.number" }));
      }),
      recentHospitalization: yup.boolean().nullable(),
      recentHospitalizationSynopsis: yup.string().nullable(),
      recentIncident: yup.string().oneOf(Object.values(YesNoDontKnowEnum)).nullable()
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const submitCareProfile = useCallback(async () => {
    const valuesWithoutErrors = produce(form.getValues(), draftValues => {
      Object.keys(form.formState.errors).forEach(field => {
        delete draftValues[field as keyof FormData];
      });
    });

    setBaseErrors([]);

    const developmentalOrEducationalConcerns = valuesWithoutErrors.developmentalOrEducationalConcerns;
    const pastYearHospitalizations = valuesWithoutErrors.pastYearHospitalizations?.toString();

    return updateCareProfile({
      variables: {
        ...valuesWithoutErrors,
        developmentalOrEducationalConcerns: developmentalOrEducationalConcerns && {
          developmentalOrEducationalConcernOptions: developmentalOrEducationalConcerns,
          other: developmentalOrEducationalConcerns.includes(CareProfileDevelopmentalOrEducationalConcernEnum.OTHER)
            ? valuesWithoutErrors.developmentalOrEducationalConcernsOther
            : null
        },
        id: journey.careProfile.id,
        pastYearHospitalizations: pastYearHospitalizations ? parseInt(pastYearHospitalizations) : null
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.careProfileUpdate,
        intl,
        onError: () => {},
        onSuccess: () => {},
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.careProfile"
      });
    });
  }, [form, intl, journey.careProfile.id, updateCareProfile]);

  useEffect(() => {
    const debouncedSubmit = debounce(submitCareProfile, 2000);
    const subscription = form.watch(() => form.handleSubmit(debouncedSubmit, debouncedSubmit)());
    return () => subscription.unsubscribe();
  }, [form, submitCareProfile]);

  const detailsForMedication = (medication: Medication) => [
    {
      labelKey: "pages.client.medications.list.kind",
      value:
        medication.kind && intl.formatMessage({ id: `pages.client.medications.list.kindOptions.${medication.kind}` })
    },
    { labelKey: "pages.client.medications.list.indication", value: medication.reasonForTaking },
    { labelKey: "pages.client.medications.list.dosageAndFrequency", value: medication.dosage },
    { labelKey: "pages.client.medications.list.refillFrequency", value: medication.refillFrequency },
    { labelKey: "pages.client.medications.list.instructions", value: medication.instructions },
    {
      labelKey: "pages.client.medications.list.format",
      value:
        medication.format.formatOption === MedicationFormatEnum.OTHER
          ? medication.format.other
          : medication.format.formatOption &&
            intl.formatMessage({ id: `pages.client.medications.list.formatOptions.${medication.format.formatOption}` })
    },
    { labelKey: "pages.client.medications.list.prescriber", value: medication.prescriber },
    { labelKey: "pages.client.medications.list.refillFrequency", value: medication.refillFrequency },
    { labelKey: "pages.client.medications.list.pharmacy", value: medication.pharmacy },
    { labelKey: "pages.client.medications.list.prescriptionNumber", value: medication.prescriptionNumber },
    {
      labelKey: "pages.client.medications.list.paymentType",
      value:
        medication.paymentType &&
        intl.formatMessage({ id: `pages.client.medications.list.paymentTypeOptions.${medication.paymentType}` })
    },
    { labelKey: "pages.client.medications.list.notes", value: medication.notes }
  ];

  const detailsForProvider = (provider: Provider) => [
    {
      labelKey: "pages.client.providers.list.kind",
      value: provider.kind && intl.formatMessage({ id: `pages.client.providers.list.kindOptions.${provider.kind}` })
    },
    { labelKey: "pages.client.providers.list.specialty", value: provider.specialty },
    { labelKey: "pages.client.providers.list.practiceName", value: provider.practiceName },
    { labelKey: "pages.client.providers.list.address", value: provider.address },
    { labelKey: "pages.client.providers.list.phone", value: provider.phone },
    { labelKey: "pages.client.providers.list.fax", value: provider.fax },
    { labelKey: "pages.client.providers.list.website", value: provider.website },
    { labelKey: "pages.client.providers.list.patientPortal", value: provider.patientPortal },
    { labelKey: "pages.client.providers.list.notes", value: provider.notes }
  ];

  const detailsForDiagnosis = (diagnosis: Diagnosis) => [
    { labelKey: "models.diagnosis.date", value: diagnosis.date },
    {
      labelKey: "models.diagnosis.severity",
      value: diagnosis.severity && intl.formatMessage({ id: `models.diagnosis.severityOptions.${diagnosis.severity}` })
    },
    {
      labelKey: "models.diagnosis.control",
      value: diagnosis.control && intl.formatMessage({ id: `models.diagnosis.controlOptions.${diagnosis.control}` })
    }
  ];

  const booleanOptions = optionsForBoolean(intl);
  const developmentalOrEducationalConcernsOptions = optionsForDevelopmentalOrEducationalConcerns(intl);
  const medicationManagementStatusOptions = optionsForMedicationManagementStatus(intl);
  const severityOptions = optionsForSeverity(intl);
  const yesNoDontKnowOptions = optionsForYesNoDontKnow(intl);

  const recentHospitalization = form.watch("recentHospitalization");

  return (
    <>
      <Form hookForm={form}>
        <BaseErrors errors={baseErrors} />
        <div className={styles.formSectionRow}>
          <div className={styles.formControlsColumn}>
            <div className={styles.rollupSection}>
              <BodyCopy>
                <FormattedMessage
                  id="forms.careProfileNew.diagnosesLabel"
                  values={{ count: journey.diagnoses.length }}
                />
              </BodyCopy>
              {journey.diagnoses.map(diagnosis => (
                <CareProfileRollupItem
                  details={detailsForDiagnosis(diagnosis)}
                  item={diagnosis}
                  key={diagnosis.id}
                  onDelete={() => {
                    destroyDiagnosis({ id: diagnosis.id });
                  }}
                  onEdit={() => {
                    setEditingDiagnosis(diagnosis);
                    diagnosisOpener.open();
                  }}
                  title={diagnosis.medicalCondition?.consumerName ?? (diagnosis.conditionOther as string)}
                />
              ))}
            </div>
            <Button
              fill="clear"
              onClick={() => {
                setEditingDiagnosis(undefined);
                diagnosisOpener.open();
              }}
            >
              <IconBase icon="add" slot="start" />
              <FormattedMessage id="dictionary.new" />
            </Button>
          </div>
          <div className={styles.formControlsColumn}>
            <div className={styles.rollupSectionsColumn}>
              <div>
                <div className={styles.rollupSection}>
                  <BodyCopy>
                    <FormattedMessage
                      id="forms.careProfileNew.providersLabel"
                      values={{ count: journey.providers.length }}
                    />
                  </BodyCopy>
                  {journey.providers.map(provider => (
                    <CareProfileRollupItem
                      details={detailsForProvider(provider)}
                      item={provider}
                      key={provider.id}
                      onDelete={() => {
                        destroyProvider({ id: provider.id });
                      }}
                      onEdit={() => {
                        setEditingProvider(provider);
                        providerOpener.open();
                      }}
                      title={provider.name}
                    />
                  ))}
                </div>
                <Button
                  fill="clear"
                  onClick={() => {
                    setEditingProvider(undefined);
                    providerOpener.open();
                  }}
                >
                  <IconBase icon="add" slot="start" />
                  <FormattedMessage id="dictionary.new" />
                </Button>
              </div>

              <div>
                <div className={styles.rollupSection}>
                  <BodyCopy>
                    <FormattedMessage
                      id="forms.careProfileNew.medicationsLabel"
                      values={{ count: journey.medications.length }}
                    />
                  </BodyCopy>
                  {journey.medications.map(medication => (
                    <CareProfileRollupItem
                      details={detailsForMedication(medication)}
                      item={medication}
                      key={medication.id}
                      onDelete={() => {
                        destroyMedication({ id: medication.id });
                      }}
                      onEdit={() => {
                        setEditingMedication(medication);
                        medicationOpener.open();
                      }}
                      title={medication.name}
                    />
                  ))}
                </div>
                <Button
                  fill="clear"
                  onClick={() => {
                    setEditingMedication(undefined);
                    medicationOpener.open();
                  }}
                >
                  <IconBase icon="add" slot="start" />
                  <FormattedMessage id="dictionary.new" />
                </Button>
              </div>
            </div>
          </div>
          <div className={styles.formControlsColumn}>
            <CareProfileFormCondition<FormData>
              careProfile={careProfile}
              fieldName={CareProfileFieldNameEnum.HIGH_RISK_PREGNANCY}
              fieldRules={careProfileFieldRules}
              form={form}
              formField="highRiskPregnancy"
              scope={CareProfileFieldScopeEnum.CARE_PROFILE}
            >
              <Select<FormData>
                clearable
                control={form.control}
                error={form.formState.errors.highRiskPregnancy}
                label={intl.formatMessage({ id: "forms.careProfileNew.highRiskPregnancyLabel" })}
                name="highRiskPregnancy"
                options={yesNoDontKnowOptions}
              />
            </CareProfileFormCondition>
            <CareProfileFormCondition<FormData>
              careProfile={careProfile}
              fieldName={CareProfileFieldNameEnum.DEVELOPMENTAL_OR_EDUCATIONAL_CONCERNS}
              fieldRules={careProfileFieldRules}
              form={form}
              formField="developmentalOrEducationalConcerns"
              scope={CareProfileFieldScopeEnum.CARE_PROFILE}
            >
              <Select<FormData>
                control={form.control}
                error={form.formState.errors.developmentalOrEducationalConcerns}
                isMulti
                label={intl.formatMessage({ id: "forms.careProfileNew.developmentalOrEducationalConcernsLabel" })}
                mutuallyExclusiveValues={[
                  CareProfileDevelopmentalOrEducationalConcernEnum.NONE,
                  CareProfileDevelopmentalOrEducationalConcernEnum.UNKNOWN
                ]}
                name="developmentalOrEducationalConcerns"
                options={developmentalOrEducationalConcernsOptions}
              />
            </CareProfileFormCondition>
            <CareProfileFormCondition<FormData>
              careProfile={careProfile}
              fieldName={CareProfileFieldNameEnum.DEVELOPMENTAL_OR_EDUCATIONAL_CONCERNS_OTHER}
              fieldRules={careProfileFieldRules}
              form={form}
              formField="developmentalOrEducationalConcernsOther"
              otherField="developmentalOrEducationalConcerns"
              otherValue={CareProfileDevelopmentalOrEducationalConcernEnum.OTHER}
              scope={CareProfileFieldScopeEnum.CARE_PROFILE}
            >
              <CareProfileInput<FormData>
                error={form.formState.errors.developmentalOrEducationalConcernsOther}
                label={intl.formatMessage({ id: "forms.careProfileNew.developmentalOrEducationalConcernsOtherLabel" })}
                register={form.register("developmentalOrEducationalConcernsOther")}
                spellcheck
              />
            </CareProfileFormCondition>
            <CareProfileFormCondition<FormData>
              careProfile={careProfile}
              fieldName={CareProfileFieldNameEnum.RECENT_HOSPITALIZATION}
              fieldRules={careProfileFieldRules}
              form={form}
              formField="recentHospitalization"
              scope={CareProfileFieldScopeEnum.CARE_PROFILE}
            >
              <BooleanRadioButtonGroup
                allowDeselection
                control={form.control}
                error={form.formState.errors.recentHospitalization}
                inTracker={false}
                label={intl.formatMessage({ id: "forms.careProfileNew.recentHospitalizationLabel" })}
                name="recentHospitalization"
                options={booleanOptions}
              />
            </CareProfileFormCondition>
            <CareProfileFormCondition<FormData>
              careProfile={careProfile}
              condition={!!recentHospitalization}
              fieldName={CareProfileFieldNameEnum.RECENT_HOSPITALIZATION_SYNOPSIS}
              fieldRules={careProfileFieldRules}
              form={form}
              formField="recentHospitalizationSynopsis"
              scope={CareProfileFieldScopeEnum.CARE_PROFILE}
            >
              <CareProfileInput
                error={form.formState.errors.recentHospitalizationSynopsis}
                label={intl.formatMessage({
                  id: "forms.careProfileNew.recentHospitalizationSynopsisLabel"
                })}
                register={form.register("recentHospitalizationSynopsis")}
                spellcheck
              />
            </CareProfileFormCondition>
            <CareProfileFormCondition<FormData>
              careProfile={careProfile}
              fieldName={CareProfileFieldNameEnum.PAST_YEAR_HOSPITALIZATIONS}
              fieldRules={careProfileFieldRules}
              form={form}
              formField="pastYearHospitalizations"
              scope={CareProfileFieldScopeEnum.CARE_PROFILE}
            >
              <CareProfileInput<FormData>
                error={form.formState.errors.pastYearHospitalizations}
                label={intl.formatMessage({ id: "forms.careProfileNew.pastYearHospitalizationsLabel" })}
                register={form.register("pastYearHospitalizations")}
                step="1"
                type="number"
              />
            </CareProfileFormCondition>
            <CareProfileFormCondition<FormData>
              careProfile={careProfile}
              fieldName={CareProfileFieldNameEnum.RECENT_INCIDENT}
              fieldRules={careProfileFieldRules}
              form={form}
              formField="recentIncident"
              scope={CareProfileFieldScopeEnum.CARE_PROFILE}
            >
              <Select<FormData>
                clearable
                control={form.control}
                error={form.formState.errors.recentIncident}
                label={intl.formatMessage({ id: "forms.careProfileNew.recentIncidentLabel" })}
                name="recentIncident"
                options={yesNoDontKnowOptions}
              />
            </CareProfileFormCondition>
            <CareProfileFormCondition<FormData>
              careProfile={careProfile}
              fieldName={CareProfileFieldNameEnum.LIFE_THREATENING_CONDITION}
              fieldRules={careProfileFieldRules}
              form={form}
              formField="lifeThreateningCondition"
              scope={CareProfileFieldScopeEnum.CARE_PROFILE}
            >
              <Select<FormData>
                clearable
                control={form.control}
                error={form.formState.errors.lifeThreateningCondition}
                label={intl.formatMessage({
                  id: "forms.careProfileNew.lifeThreateningConditionLabel"
                })}
                name="lifeThreateningCondition"
                options={yesNoDontKnowOptions}
              />
            </CareProfileFormCondition>
            <CareProfileFormCondition<FormData>
              careProfile={careProfile}
              fieldName={CareProfileFieldNameEnum.LONG_TERM_CONDITION}
              fieldRules={careProfileFieldRules}
              form={form}
              formField="longTermCondition"
              scope={CareProfileFieldScopeEnum.CARE_PROFILE}
            >
              <Select<FormData>
                clearable
                control={form.control}
                error={form.formState.errors.longTermCondition}
                label={intl.formatMessage({ id: "forms.careProfileNew.longTermConditionLabel" })}
                name="longTermCondition"
                options={yesNoDontKnowOptions}
              />
            </CareProfileFormCondition>
            <CareProfileFormCondition<FormData>
              careProfile={careProfile}
              fieldName={CareProfileFieldNameEnum.MEDICATIONS_AFFORDABLE}
              fieldRules={careProfileFieldRules}
              form={form}
              formField="medicationsAffordable"
              scope={CareProfileFieldScopeEnum.CARE_PROFILE}
            >
              <Select<FormData>
                clearable
                control={form.control}
                error={form.formState.errors.medicationsAffordable}
                label={intl.formatMessage({ id: "forms.careProfileNew.medicationsAffordableLabel" })}
                name="medicationsAffordable"
                options={yesNoDontKnowOptions}
              />
            </CareProfileFormCondition>
            <CareProfileFormCondition<FormData>
              careProfile={careProfile}
              fieldName={CareProfileFieldNameEnum.MEDICATION_MANAGEMENT_STATUS}
              fieldRules={careProfileFieldRules}
              form={form}
              formField="medicationManagementStatus"
              scope={CareProfileFieldScopeEnum.CARE_PROFILE}
            >
              <Select<FormData>
                clearable
                control={form.control}
                error={form.formState.errors.medicationManagementStatus}
                label={intl.formatMessage({ id: "forms.careProfileNew.medicationManagementStatusLabel" })}
                name="medicationManagementStatus"
                options={medicationManagementStatusOptions}
              />
            </CareProfileFormCondition>
            <CareProfileFormCondition<FormData>
              careProfile={careProfile}
              fieldName={CareProfileFieldNameEnum.PAIN_SEVERITY}
              fieldRules={careProfileFieldRules}
              form={form}
              formField="painSeverity"
              scope={CareProfileFieldScopeEnum.CARE_PROFILE}
            >
              <Select<FormData>
                clearable
                control={form.control}
                error={form.formState.errors.painSeverity}
                label={intl.formatMessage({ id: "forms.careProfileNew.painSeverityLabel" })}
                name="painSeverity"
                options={severityOptions}
              />
            </CareProfileFormCondition>
          </div>
        </div>
      </Form>
      <Modal
        opener={medicationOpener}
        ref={medicationModalRef}
        title={
          editingMedication
            ? intl.formatMessage({ id: "models.client.medication.updateMedication" })
            : intl.formatMessage({ id: "models.client.medication.addNewMedication" })
        }
      >
        {({ modalDisplayed }) => (
          <MedicationForm
            autofocus={modalDisplayed}
            journeyId={journey.id}
            medication={editingMedication}
            onCancel={medicationOpener.close}
            onError={() => {}}
            onSuccess={medicationOpener.close}
          />
        )}
      </Modal>
      <Modal
        opener={providerOpener}
        ref={providerModalRef}
        title={
          editingProvider
            ? intl.formatMessage({ id: "models.careProfile.updateProvider" })
            : intl.formatMessage({ id: "models.careProfile.addNewProvider" })
        }
      >
        {({ modalDisplayed }) => (
          <ProviderForm
            autofocus={modalDisplayed}
            journeyId={journey.id}
            kindEditable={!editingProvider}
            onCancel={providerOpener.close}
            onError={() => {}}
            onSuccess={providerOpener.close}
            provider={editingProvider}
          />
        )}
      </Modal>
      <Modal
        opener={diagnosisOpener}
        ref={diagnosisModalRef}
        title={
          editingDiagnosis
            ? intl.formatMessage({ id: "models.careProfile.updateDiagnosis" })
            : intl.formatMessage({ id: "models.careProfile.addNewDiagnosis" })
        }
      >
        {() => (
          <DiagnosisForm
            diagnosis={editingDiagnosis}
            journeyId={journey.id}
            onCancel={diagnosisOpener.close}
            onError={() => {}}
            onSuccess={diagnosisOpener.close}
          />
        )}
      </Modal>
    </>
  );
};

export default CareProfileMedicalForm;
